import { useCallback, useEffect, useMemo } from "react";
import { useHistory } from "react-router";

import { FlightError } from "@b2bportal/air-booking-api";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import {
  Payment,
  type PaymentError,
  Product,
  type ProductError,
  type PurchaseError,
  PurchaseErrorEnum,
} from "@b2bportal/purchase-api";
import {
  AirChfarExerciseDiscountSelectors,
  CartFulfillErrorModal,
  CartFulfillEventType,
  cartFulfillSelectors,
  CartQuoteEventType,
  cartQuoteSelectors,
  FlightPassengerEventTypesV2,
  useCheckoutSend,
  useCheckoutStateSelector as useSelector,
  useFlightErrorConfig,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import { CallState, CHFAR_ID_QUERY_PARAM } from "@hopper-b2b/types";
import { getEnvVariables } from "@hopper-b2b/utilities";

import {
  reportUnexpectedRevokeFailure,
  revokeEnrollmentForNeedsNewEnrollmentError,
} from "../../../../checkout/states/payments/needsNewEnrollment";
import { NupayPaymentError } from "../../../../checkout/states/payments/types";
import { RedirectLoader } from "../../../../components/Body/RedirectLoader";
import { ErrorScreen } from "../../../../components/ErrorScreen";
import { LoadingScreen } from "../../../../components/Slots";
import { useCheckoutMyTripsNavigation } from "../../../../hooks/useCheckoutMyTripsNavigation";
import { useNubankFeatureFlagsContext } from "../../../../utils/featureFlagsContext";
import { PATH_FLIGHTS_SEARCH } from "../../../../utils/urlPaths";
import { Event, type TEvent } from "../../events";
import "./styles.scss";

export const CartFulfillComponent = () => {
  const { t } = useI18nContext();
  const isErrorModalOpen = useSelector(
    cartFulfillSelectors.getOpenCartFulfillErrorModal
  );

  const loadingSteps = [
    {
      title: t("scheduleFulfillLoading.step1Title"),
    },
    {
      title: t("scheduleFulfillLoading.step2Title"),
    },
    {
      title: t("scheduleFulfillLoading.step3Title"),
    },
  ];

  const callState = useSelector(cartFulfillSelectors.getCartFulfillCallState);

  return isErrorModalOpen ? (
    <MappedErrorModal />
  ) : (
    <LoadingScreen
      loading={callState === CallState.InProcess}
      averageLoadingTime={getEnvVariables("cartFulfillLoadingTime") as number}
      className=".schedule-fulfill-loading"
      loadingSteps={loadingSteps}
    />
  );
};

/**
 * New error modal using centralized config
 */
const CentralizedMappedErrorModal = () => {
  const history = useHistory();
  const send = useCheckoutSend<TEvent>();
  const cartErrorModal = useSelector(cartFulfillSelectors.getCartFulfillError);
  const chfarId = useSelector(AirChfarExerciseDiscountSelectors.getChfarId);
  const headError = useMemo(
    () => cartErrorModal?.data?.[0] as PurchaseError | undefined,
    [cartErrorModal?.data]
  );

  const navigateToSearch = () => {
    if (chfarId) {
      history.push(
        `/${PATH_FLIGHTS_SEARCH}?${CHFAR_ID_QUERY_PARAM}=${chfarId}`
      );
    } else {
      history.push("/" + PATH_FLIGHTS_SEARCH);
    }
  };
  const navigateToLegacyMyTrips = useCallback(() => {
    history.push("/trips");
  }, [history]);
  const { navigateToMyTrips: navigateFromCheckout, nativeHandoffPending } =
    useCheckoutMyTripsNavigation(navigateToLegacyMyTrips);
  const navigateToMyTrips = useCallback(() => {
    send(CartFulfillEventType.CLEAR_CART_FULFILL_ERROR);
    navigateFromCheckout();
  }, [navigateFromCheckout, send]);

  const { getPurchaseErrorProps, getDefaultProps } = useFlightErrorConfig({
    context: "CART_FULFILL",
    handlers: {
      navigateToFlightSearch: () => {
        send(CartFulfillEventType.CLEAR_CART_FULFILL_ERROR);
        navigateToSearch();
      },
      navigateToMyTrips,
      navigateToConfirmation: () => {
        send(CartFulfillEventType.CLEAR_CART_FULFILL_ERROR);
        send(Event.GO_TO_BOOKING_CONFIRMATION);
      },
      openPassengerPicker: () => {
        send(CartFulfillEventType.CLEAR_CART_FULFILL_ERROR);
        send(Event.GO_TO_PASSENGER_SELECT);
      },
      goBackToPassengerInfo: () => {
        send(CartFulfillEventType.CLEAR_CART_FULFILL_ERROR);
        send(Event.GO_TO_PASSENGER_SELECT);
      },
      openPassportForm: () => {
        send(CartFulfillEventType.CLEAR_CART_FULFILL_ERROR);
        send(FlightPassengerEventTypesV2.OPEN_PASSPORT_FORM);
      },
      retryQuote: () => {
        send(CartFulfillEventType.CLEAR_CART_FULFILL_ERROR);
        send(CartQuoteEventType.RETRY_QUOTE);
      },
      clearError: () => {
        send(CartFulfillEventType.CLEAR_CART_FULFILL_ERROR);
      },
    },
  });

  const getErrorModalProps = () => {
    if (
      cartErrorModal?.type === CartFulfillErrorModal.FailedPolling &&
      cartErrorModal.data?.[0]
    ) {
      return getPurchaseErrorProps(cartErrorModal.data[0] as PurchaseError);
    }
    return getDefaultProps();
  };

  const errorModalProps = getErrorModalProps();

  useEffect(() => {
    if (!headError) {
      return;
    }

    revokeEnrollmentForNeedsNewEnrollmentError(headError).catch(
      reportUnexpectedRevokeFailure
    );
  }, [headError]);

  return (
    <>
      {nativeHandoffPending ? <RedirectLoader /> : null}
      <ErrorScreen
        title={errorModalProps.title}
        subtitle={errorModalProps.subtitle}
        primaryButtonText={errorModalProps.primaryButtonText}
        primaryOnClick={errorModalProps.primaryOnClick}
        secondaryButtonText={errorModalProps.secondaryButtonText}
        secondaryOnClick={errorModalProps.secondaryOnClick}
        className={errorModalProps.className ?? "error"}
      />
    </>
  );
};

/**
 * Legacy error modal (original implementation)
 */
const LegacyMappedErrorModal = () => {
  const { t } = useI18nContext();
  const history = useHistory();
  const send = useCheckoutSend<TEvent>();
  const cartErrorModal = useSelector(cartFulfillSelectors.getCartFulfillError);
  const chfarId = useSelector(AirChfarExerciseDiscountSelectors.getChfarId);
  const headError = useMemo(
    () => cartErrorModal?.data?.[0] as PurchaseError | undefined,
    [cartErrorModal?.data]
  );

  const clearError = () => send(CartFulfillEventType.CLEAR_CART_FULFILL_ERROR);

  const searchAgain = () => {
    clearError();
    //ChFAR exercise
    if (chfarId)
      history.push(
        `/${PATH_FLIGHTS_SEARCH}?${CHFAR_ID_QUERY_PARAM}=${chfarId}`
      );
    else history.push("/" + PATH_FLIGHTS_SEARCH);
  };
  const updateTravelers = () => {
    clearError();
    send(Event.GO_TO_PASSENGER_SELECT);
  };
  const goToConfirmation = () => {
    clearError();
    send(Event.GO_TO_BOOKING_CONFIRMATION);
  };

  const allowedQuoteRetry = useSelector(cartQuoteSelectors.getAllowQuoteRetry);
  const retryQuote = () => {
    clearError();
    send(CartQuoteEventType.RETRY_QUOTE);
  };
  const retryQuoteOrSearchAgain = {
    ...(allowedQuoteRetry
      ? {
          primaryButtonText: t("checkoutError.tryAgain"),
          primaryOnClick: retryQuote,
          secondaryButtonText: t("checkoutError.findNewFlight"),
          secondaryOnClick: searchAgain,
        }
      : {
          primaryButtonText: t("checkoutError.findNewFlight"),
          primaryOnClick: searchAgain,
        }),
  };
  const DEFAULT_ERROR_MODEL_PROPS = {
    title: t("checkoutError.genericErrorTitle"),
    subtitle: t("checkoutError.genericErrorSubtitle"),
    className: "error",
    ...retryQuoteOrSearchAgain,
  };

  const getFlightError = (flightError: FlightError) => {
    switch (flightError) {
      case FlightError.CheckInMinimumAgeNotMet:
        return {
          title: t("checkoutError.checkInMinimumAgeNotMetTitle"),
          subtitle: t("checkoutError.checkInMinimumAgeNotMetSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.DuplicateBooking:
        return {
          title: t("checkoutError.duplicateBookingTitle"),
          subtitle: t("checkoutError.duplicateBookingSubtitle"),
          primaryButtonText: t("checkoutError.acknowlegement"),
          primaryOnClick: goToConfirmation,
          className: "warn",
        };
      case FlightError.ExpiringPassport:
        return {
          title: t("checkoutError.expiringPassportTitle"),
          subtitle: t("checkoutError.expiringPassportSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.ExpiredPassport:
        return {
          title: t("checkoutError.expiredPassportTitle"),
          subtitle: t("checkoutError.expiredPassportSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.IllegalLapInfantKTN:
        return {
          title: t("checkoutError.illegalLapInfantKTNTitle"),
          subtitle: t("checkoutError.illegalLapInfantKTNSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.IllegalLapInfantRedress:
        return {
          title: t("checkoutError.illegalLapInfantRedressTitle"),
          subtitle: t("checkoutError.illegalLapInfantRedressSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          className: "missing",
          primaryOnClick: updateTravelers,
        };
      case FlightError.Inactivity:
        return {
          title: t("checkoutError.flightsInActivityTitle"),
          subtitle: t("checkoutError.flightsInActivitySubtitle"),
          primaryButtonText: t("checkoutError.tryAgain"),
          className: "timeout",
          ...retryQuoteOrSearchAgain,
        };
      case FlightError.InvalidCustomer:
        return {
          title: t("checkoutError.invalidCustomerTitle"),
          subtitle: t("checkoutError.invalidCustomerSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.InvalidPassengers:
        return {
          title: t("checkoutError.invalidPassenger"),
          subtitle: t("checkoutError.invalidPassengerSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          className: "missing",
          primaryOnClick: updateTravelers,
        };
      case FlightError.InvalidPassengerName:
        return {
          title: t("checkoutError.invalidPassenger"),
          subtitle: t("checkoutError.invalidPassengerName"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.InvalidPassportNumber:
        return {
          title: t("checkoutError.invalidPassportNumberTitle"),
          subtitle: t("checkoutError.invalidPassportNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.LapInfantTooOld:
        return {
          title: t("checkoutError.lapInfantTooOldTitle"),
          subtitle: t("checkoutError.lapInfantTooOldSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.LapInfantsUnsupported:
        return {
          title: t("checkoutError.lapInfantsUnsupportedTitle"),
          subtitle: t("checkoutError.lapInfantsUnsupportedSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.MalformedKnownTravelerNumber:
        return {
          title: t("checkoutError.malformedKnownTravelerNumberTitle"),
          subtitle: t("checkoutError.malformedKnownTravelerNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.MalformedRedressNumber:
        return {
          title: t("checkoutError.malformedRedressNumberTitle"),
          subtitle: t("checkoutError.malformedRedressNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.MissingAirlineLocator:
        return {
          title: t("checkoutError.missingAirlineLocatorTitle"),
          subtitle: t("checkoutError.missingAirlineLocatorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
          className: "error",
        };
      case FlightError.MissingNationality:
        return {
          title: t("checkoutError.missingNationalityTitle"),
          subtitle: t("checkoutError.missingNationalitySubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
          className: "missing",
        };
      case FlightError.MissingPassport:
        return {
          title: t("checkoutError.missingPassportTitle"),
          subtitle: t("checkoutError.missingPassportSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.MissingPassportNumber:
        return {
          title: t("checkoutError.missingPassportNumberTitle"),
          subtitle: t("checkoutError.missingPassportNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.MissingPassportOrNationalId:
        return {
          title: t("checkoutError.missingPassportOrNationalIdTitle"),
          subtitle: t("checkoutError.missingPassportOrNationalIdSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.NoAdultPassenger:
        return {
          title: t("checkoutError.noAdultPassengerTitle"),
          subtitle: t("checkoutError.noAdultPassengerSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.NoContactInformation:
        return {
          title: t("checkoutError.noContactInformationTitle"),
          subtitle: t("checkoutError.noContactInformationSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.NoTicketlessResponse:
        return {
          title: t("checkoutError.noTicketlessResponseTitle"),
          subtitle: t("checkoutError.noTicketlessResponseSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
          className: "error",
        };
      case FlightError.PassengerNameTooShort:
        return {
          title: t("checkoutError.invalidPassenger"),
          subtitle: t("checkoutError.passengerNameTooShortSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.ProviderError:
        return {
          title: t("checkoutError.providerErrorTitle"),
          subtitle: t("checkoutError.providerErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
          className: "error",
        };
      case FlightError.SeatedInfantsUnsupported:
        return {
          title: t("checkoutError.seatedInfantsUnsupportedTitle"),
          subtitle: t("checkoutError.seatedInfantsUnsupportedSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "error",
        };
      case FlightError.TicketingNotStarted:
        return {
          title: t("checkoutError.ticketingNotStartedTitle"),
          subtitle: t("checkoutError.ticketingNotStartedSubtitle"),
          primaryButtonText: "",
          primaryOnClick: goToConfirmation,
          className: "error",
        };
      case FlightError.TicketingUnconfirmed:
        return {
          title: t("checkoutError.ticketingUnconfirmedTitle"),
          subtitle: t("checkoutError.ticketingUnconfirmedSubtitle"),
          primaryButtonText: "",
          primaryOnClick: goToConfirmation,
        };
      case FlightError.TicketingUnknown:
        return {
          title: t("checkoutError.ticketingUnknownTitle"),
          subtitle: t("checkoutError.ticketingUnknownSubtitle"),
          primaryButtonText: "",
          primaryOnClick: goToConfirmation,
        };
      case FlightError.TooManyLapInfants:
        return {
          title: t("checkoutError.tooManyLapInfantsTitle"),
          subtitle: t("checkoutError.tooManyLapInfantsSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.TooManyPassengers:
        return {
          title: t("checkoutError.tooManyPassengersTitle"),
          subtitle: t("checkoutError.tooManyPassengersSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.TransientServiceError:
        return {
          title: t("checkoutError.transientServiceErrorTitle"),
          subtitle: t("checkoutError.transientServiceErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
          className: "error",
        };
      case FlightError.UnknownSabreAppError:
        return {
          title: t("checkoutError.unknownSabreAppErrorTitle"),
          subtitle: t("checkoutError.unknownSabreAppErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
          className: "error",
        };
      case FlightError.UnsupportedNameCharacters:
        return {
          title: t("checkoutError.invalidPassenger"),
          subtitle: t("checkoutError.unsupportedNameCharactersSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
          className: "missing",
        };
      case FlightError.UpgradeRequired:
        return {
          title: t("checkoutError.upgradeRequiredTitle"),
          subtitle: t("checkoutError.unknownSabreAppErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
          className: "error",
        };
      // default FlightErrors
      case FlightError.DuplicatedNationalId:
      case FlightError.DuplicatedPassport:
      case FlightError.ExcessiveActivity:
      case FlightError.ExpiredNationalId:
      case FlightError.ExpiringNationalId:
      case FlightError.ExtraBagsError:
      case FlightError.InvalidPassportOrNationalIdNumber:
      case FlightError.MissingNationalId:
      case FlightError.NoAvailability:
      case FlightError.PassengerNotFound:
      case FlightError.PassthroughPaymentError:
      case FlightError.SessionClosed:
      case FlightError.ShopAsyncError:
      case FlightError.UnexpectedPassengers:
      case FlightError.Unknown:
      case FlightError.UnsupportedPaymentKind:
        return DEFAULT_ERROR_MODEL_PROPS;
      default:
        return ensureExhaustive(flightError, DEFAULT_ERROR_MODEL_PROPS);
    }
  };

  const getProductError = (error: ProductError) => {
    switch (error.value.type) {
      case Product.Flight:
      case Product.MultiProviderFlight:
        return getFlightError(error.value.value as FlightError);
      case Product.Seats:
        return getFlightError(error.value.value as FlightError);
      default:
        return DEFAULT_ERROR_MODEL_PROPS;
    }
  };

  const getNupayPaymentError = (error: NupayPaymentError) => {
    switch (error) {
      case NupayPaymentError.GetUserFailure:
      case NupayPaymentError.OpenSessionFailure:
      case NupayPaymentError.QuoteIdMismatch:
      case NupayPaymentError.UnknownFailure:
      default:
        return DEFAULT_ERROR_MODEL_PROPS;
    }
  };

  const getPaymentError = (error: PaymentError) => {
    switch (error.value.type) {
      case Payment.NupayPayment:
      case Payment.EbanxNupayPayment:
        return getNupayPaymentError(error.value.value as NupayPaymentError);
      default:
        return DEFAULT_ERROR_MODEL_PROPS;
    }
  };

  const getErrorModalProps = (
    type?: CartFulfillErrorModal,
    data?: PurchaseError[]
  ) => {
    switch (type) {
      case CartFulfillErrorModal.FailedPolling: {
        const head = data?.[0];
        switch (head?.Error) {
          case PurchaseErrorEnum.NoAvailability:
            return {
              title: t("checkoutError.noAvailabilityTitle"),
              subtitle: t("checkoutError.noAvailabilitySubtitle"),
              primaryButtonText: t("checkoutError.searchAgain"),
              primaryOnClick: searchAgain,
              className: "error",
            };
          case PurchaseErrorEnum.PaymentError:
            return getPaymentError(head as PaymentError);
          case PurchaseErrorEnum.ProductError:
            return getProductError(head as ProductError);
          case PurchaseErrorEnum.ExpiredFulfillToken:
            return {
              title: t("checkoutError.noAvailabilityTitle"),
              subtitle: t("checkoutError.noAvailabilitySubtitle"),
              primaryButtonText: t("checkoutError.searchAgain"),
              primaryOnClick: searchAgain,
              className: "error",
            };
          default:
            return DEFAULT_ERROR_MODEL_PROPS;
        }
      }
      default:
        return DEFAULT_ERROR_MODEL_PROPS;
    }
  };
  const errorModalProps = getErrorModalProps(
    cartErrorModal?.type,
    cartErrorModal?.data as PurchaseError[]
  );

  useEffect(() => {
    if (!headError) {
      return;
    }

    revokeEnrollmentForNeedsNewEnrollmentError(headError).catch(
      reportUnexpectedRevokeFailure
    );
  }, [headError]);

  return <ErrorScreen {...errorModalProps} />;
};

/**
 * Error modal component that switches between legacy and centralized config
 * based on feature flag
 */
export const MappedErrorModal = () => {
  const { enableCentralizedErrorConfig } = useNubankFeatureFlagsContext();

  if (enableCentralizedErrorConfig) {
    return <CentralizedMappedErrorModal />;
  }

  return <LegacyMappedErrorModal />;
};
